import { Component } from '@theme/component';

/**
 * Custom tabs element that allows switching between tabbed content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} tabpanels
 *
 * @extends {Component<Refs>}
 */
class TabsCustom extends Component {
  requiredRefs = ['tablist', 'tabs', 'tabpanels'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #animAbort;

  static get observedAttributes() {
    return ['selected'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this._vertical = this.getAttribute('orientation') === 'vertical';
    this._syncHash = this.hasAttribute('sync-hash');

    this.#animAbort?.abort(); // in case we reconnect mid-animation

    this._setup();

    if (this._syncHash) {
      const { signal } = this.#controller;
      window.addEventListener(
        'hashchange',
        () => {
          const idx = this.refs.tabpanels.findIndex((p) => `#${p.id}` === location.hash);
          if (idx >= 0) this._select(idx, { focus: false });
        },
        { signal: signal }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#animAbort?.abort();
  }

  /**
   * @param {string} name
   */
  attributeChangedCallback(name) {
    if (name === 'selected' && this._inited) {
      const i = this._coerceSelectedIndex(this.getAttribute('selected'));
      if (i != null) this._select(i, { focus: false });
    }
  }

  _setup() {
    // Initial selection: hash > selected attr > 0
    let index = 0;
    if (this._syncHash && location.hash) {
      const h = this.refs.tabpanels.findIndex((p) => `#${p.id}` === location.hash);
      if (h >= 0) index = h;
    } else {
      const fromAttr = this._coerceSelectedIndex(this.getAttribute('selected'));
      if (fromAttr != null) index = fromAttr;
    }
    this._select(index, { focus: false });
    this._inited = true;
  }

  /**
   *
   * @param {string | number | null} sel
   */
  _coerceSelectedIndex(sel) {
    if (sel == null) return null;
    if (!Number.isNaN(+sel)) {
      const i = +sel | 0;
      return Math.max(0, Math.min(i, this.refs.tabpanels.length - 1));
    }
    const byId = this.refs.tabpanels.findIndex((p) => p.id === sel);
    return byId >= 0 ? byId : 0;
  }

  /**
   *
   * @param {number} i
   * @param {{ focus: boolean }} param1
   */
  _select(i, { focus } = { focus: false }) {
    if (i < 0 || i >= this.refs.tabs.length || !this.refs.tabs[i] || !this.refs.tabpanels[i])
      return;
    const prev = this.selectedIndex;

    if (prev === i) return;

    this.refs.tabs.forEach((tab, j) => {
      const on = j === i;

      tab.setAttribute('aria-selected', String(on));
    });

    const entering = this.refs.tabpanels[i];
    const leaving = prev >= 0 ? this.refs.tabpanels[prev] : null;
    const reduced =
      this.hasAttribute('no-anim') || matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#animAbort?.abort();
    this.#animAbort = new AbortController();

    const clearClasses = (/** @type {HTMLElement} */ el) => {
      if (!el) return;
      el.classList.remove(
        'enter-right',
        'enter-left',
        'leave-left',
        'leave-right',
        'enter-down',
        'enter-up',
        'leave-up',
        'leave-down'
      );
    };

    const forward = prev === -1 ? true : i > prev;

    entering.hidden = false;
    clearClasses(entering);

    const finishEnter = () => {
      clearClasses(entering);
      if (focus) {
        const firstFocusable = entering.querySelector('[tabindex],a,button');
        if (firstFocusable && firstFocusable instanceof HTMLElement) {
          firstFocusable.focus();
        }
      }
    };

    if (reduced) {
      finishEnter();
    } else {
      if (this._vertical) {
        entering.classList.add(forward ? 'enter-down' : 'enter-up');
      } else {
        entering.classList.add(forward ? 'enter-right' : 'enter-left');
      }
      entering.addEventListener('animationend', finishEnter, {
        signal: this.#animAbort.signal,
        once: true,
      });
    }

    if (leaving) {
      const finishLeave = () => {
        leaving.hidden = true;
        clearClasses(leaving);
      };

      if (reduced) {
        finishLeave();
      } else {
        if (this._vertical) {
          leaving.classList.add(forward ? 'leave-up' : 'leave-down');
        } else {
          leaving.classList.add(forward ? 'leave-left' : 'leave-right');
        }
        leaving.addEventListener('animationend', finishLeave, {
          signal: this.#animAbort.signal,
          once: true,
        });
      }
    }

    if (this._syncHash) history.replaceState(null, '', `#${entering.id}`);
    this.dispatchEvent(new CustomEvent('tabchange', { detail: { index: i, id: entering.id } }));
  }

  /**
   * Handle tab click events.
   * @param {MouseEvent} e - The click event.
   */
  onTabClick(e) {
    if (!(e.target instanceof HTMLButtonElement)) return;

    const i = this.refs.tabs.indexOf(e.target);
    if (i >= 0) this._select(i, { focus: false });
  }

  /**
   * Handle keydown events on the tab elements.
   * @param {KeyboardEvent} e - The keydown event.
   */
  onTabKeydown(e) {
    if (!(e.target instanceof HTMLButtonElement)) return;

    const current = this.refs.tabs.indexOf(e.target);
    if (current === -1) return;

    const horiz = !this._vertical;
    let next = null;
    switch (e.key) {
      case 'ArrowRight':
        if (horiz) next = Math.max(0, Math.min(current + 1, this.refs.tabs.length - 1));
        break;
      case 'ArrowLeft':
        if (horiz) next = Math.max(0, Math.min(current - 1, this.refs.tabs.length - 1));
        break;
      case 'ArrowDown':
        if (!horiz) next = Math.max(0, Math.min(current + 1, this.refs.tabs.length - 1));
        break;
      case 'ArrowUp':
        if (!horiz) next = Math.max(0, Math.min(current - 1, this.refs.tabs.length - 1));
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.refs.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        this._select(current, { focus: true });
        e.preventDefault();
        return;
      default:
        return;
    }
    if (next !== null && this.refs.tabs[next]) {
      this.refs.tabs[next]?.focus();
      e.preventDefault();
    }
  }

  get selectedIndex() {
    return this.refs.tabs?.findIndex((t) => t.getAttribute('aria-selected') === 'true') ?? -1;
  }
  set selectedIndex(i) {
    if (this.refs.tabs && i >= 0 && i < this.refs.tabs.length) this._select(i, { focus: false });
  }
}

customElements.define('tabs-custom', TabsCustom);
